/**
 * Copyright (C) 2026 Viasat, Inc.
 * All rights reserved.
 * The information in this software is subject to change without notice and should not be construed as a commitment by Viasat, Inc.
 *
 * Viasat Proprietary
 * The Proprietary Information provided herein is proprietary to Viasat and
 * must be protected from further distribution and use. Disclosure to others,
 * use or copying without express written authorization of Viasat, is strictly prohibited.
 */
import { Box } from '@mui/material';
import isNil from 'lodash/isNil';
import React from 'react';
import { TerminalDetails } from '../../../../../shared/entities/terminal';
import { dateToUTCDateFullDayName, secondsSinceEpochToDate } from '../../../../../utils/DateTimeUtils';
import { feetToMiles } from '../../../../../utils/DistanceUtils';
import { toFixedValueFormatter } from '../../../../../utils/StringUtils';
import { knotToMphString } from '../../../../../utils/VelocityUtils';
import CollapseGroupItem from '../../../../common/collapse_group/CollapseGroupItem';
import SpaceVehicleStatusCollapseGroupItem from '../../../../common/collapse_group/SpaceVehicleStatusCollapseGroupItem';
import appTheme from '../../../../common/theme/appTheme.json';
import { FullVehicleViewButton } from './FullVehicleViewButton';

const MODEM_TOOLTIP_MAX_WIDTH_PX = 124;
const ANTENNA_TOOLTIP_MAX_WIDTH_PX = 262;

/**
 * The Last Update row sits with the coordinates, so it dates the position rather than the record.
 * positionSourceTime is set when the position came from HaloNet instead of the terminal.
 */
const getLastUpdateText = (positionSourceTime?: number, isPositionStale?: boolean) => {
  if (isNil(positionSourceTime)) {
    return null;
  }

  const updatedAt = dateToUTCDateFullDayName(secondsSinceEpochToDate(positionSourceTime));

  return isPositionStale ? `${updatedAt} (no newer data)` : updatedAt;
};

export const renderSpaceVehicleTabHeader = (
  tailDetailsPanelTerminal: TerminalDetails | undefined,
  isFullTailViewMode: boolean,
  history: any,
) => {
  const { altitudeFeet, latitudeDeg, longitudeDeg, velocityKnots } =
    tailDetailsPanelTerminal ?? ({} as TerminalDetails);

  // A space vehicle position usually comes from HaloNet and carries its own time. Fall back to the
  // record time for a terminal that reports a position of its own.
  const positionSourceTime = tailDetailsPanelTerminal?.positionSourceTime ?? tailDetailsPanelTerminal?.sourceTime;

  // The propagated window ran out, so the position is the last point there was rather than a
  // current one. Without this the marker simply stops moving and nothing says why.
  const isPositionStale = !!tailDetailsPanelTerminal?.isPositionOutsidePropagationWindow;

  const altitudeMiles = isNil(altitudeFeet) ? null : feetToMiles(altitudeFeet);

  return (
    <React.Fragment>
      <Box
        sx={{
          backgroundColor: appTheme.secondaryBackgroundColor,
          padding: '30px 20px 30px 41px',
        }}
      >
        <SpaceVehicleStatusCollapseGroupItem terminal={tailDetailsPanelTerminal} />
        <CollapseGroupItem
          id="last_update"
          label="Last Update"
          value={getLastUpdateText(positionSourceTime, isPositionStale)}
          color={isPositionStale ? appTheme.warningTextColor : undefined}
        />
        <CollapseGroupItem
          id="latitude"
          label="Latitude"
          value={latitudeDeg}
          valueFormatter={toFixedValueFormatter(4, '°')}
        />
        <CollapseGroupItem
          id="longitude"
          label="Longitude"
          value={longitudeDeg}
          valueFormatter={toFixedValueFormatter(4, '°')}
        />
        <CollapseGroupItem
          id="altitude"
          label="Altitude"
          value={altitudeMiles}
          valueFormatter={toFixedValueFormatter(1, ' mi')}
          truncate
          tooltipMaxWidth={ANTENNA_TOOLTIP_MAX_WIDTH_PX}
        />
        <CollapseGroupItem
          id="velocity"
          label="Velocity"
          value={velocityKnots}
          truncate
          tooltipMaxWidth={MODEM_TOOLTIP_MAX_WIDTH_PX}
          valueFormatter={knotToMphString}
        />
      </Box>
      <Box>
        {isFullTailViewMode ? (
          <div className="empty_button_spacer" />
        ) : (
          <FullVehicleViewButton tailDetailsPanelTerminal={tailDetailsPanelTerminal} history={history} />
        )}
      </Box>
    </React.Fragment>
  );
};
